/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect } from 'react';
import { useLogger } from '@airasia-rn/core-logger';
import { fireGtmEvent } from '@airasia-rn/ui/utils';
import { useIsLoggedIn } from '@airasia-rn/account/hooks/account';
import {
  UHF_EVENT,
  buildUHFHeaderAvatarParams,
  buildUHFHeaderDarkModeParams,
  buildUHFHeaderLocaleCurrencyParams,
  buildUHFHeaderMenuParams,
  buildUHFHeaderLogoParams,
  buildUHFHeaderNavParams,
  buildUHFFooterParams,
  buildUHFLoginTooltipParams,
} from '../../analytics/uhf/uhf.analytics';
import { NavigationItem } from '../../widgets/WebHeaderView/types';
import { useAppConfig } from '../../../../react-native-ui/src/config/AppConfigProvider';
import { DEFAULT_LANGUAGE } from '../../../../react-native-ui/src/localization/constants';

type UseMoveWebPageAnalyticsParams = {
  isDarkMode: boolean;
  currencyText: string;
  languageText: string;
  lob?: string;
  screenName?: string;
  toggleDarkMode: () => void;
};

export const useMoveWebPageAnalytics = ({
  isDarkMode,
  currencyText,
  languageText,
  lob,
  screenName,
  toggleDarkMode,
}: UseMoveWebPageAnalyticsParams) => {
  const { logEvent } = useLogger();
  const { isLoggedIn, isLoading: isLoggedInLoading } =
    useIsLoggedIn('MoveWebPage');
  const {
    config: { locale = DEFAULT_LANGUAGE },
  } = useAppConfig();
  const resolvedScreen = screenName ?? lob ?? '';

  useEffect(() => {
    if (isLoggedInLoading) return;
    fireGtmEvent({
      loginStatus: isLoggedIn ? 'loggedin' : 'non-loggedin',
    });
  }, [isLoggedIn, isLoggedInLoading]);

  const handleAccountPress = useCallback(() => {
    logEvent(
      UHF_EVENT,
      buildUHFHeaderAvatarParams(
        isLoggedIn ? 'menu' : 'loginModal',
        lob ?? null,
        resolvedScreen,
      ),
    );
  }, [isLoggedIn, lob, logEvent, resolvedScreen]);

  const handleCurrencyPress = useCallback(() => {
    logEvent(
      UHF_EVENT,
      buildUHFHeaderLocaleCurrencyParams(
        currencyText,
        lob ?? null,
        resolvedScreen,
      ),
    );
  }, [currencyText, lob, logEvent, resolvedScreen]);

  const handleLanguagePress = useCallback(() => {
    logEvent(
      UHF_EVENT,
      buildUHFHeaderLocaleCurrencyParams(
        languageText,
        lob ?? null,
        resolvedScreen,
      ),
    );
  }, [languageText, lob, logEvent, resolvedScreen]);

  const handleLogoPress = useCallback(() => {
    logEvent(
      UHF_EVENT,
      buildUHFHeaderLogoParams('MOVE', lob ?? null, resolvedScreen),
    );
  }, [lob, logEvent, resolvedScreen]);

  const handleNavigationItemPress = useCallback(
    (item: NavigationItem) => {
      logEvent(
        UHF_EVENT,
        buildUHFHeaderNavParams(item.id, lob ?? null, resolvedScreen),
      );
    },
    [lob, logEvent, resolvedScreen],
  );

  const handleDarkModeToggle = useCallback(() => {
    logEvent(
      UHF_EVENT,
      buildUHFHeaderDarkModeParams(
        isDarkMode ? 'light_mode' : 'dark_mode',
        lob ?? null,
        resolvedScreen,
      ),
    );
    toggleDarkMode();
  }, [isDarkMode, lob, logEvent, resolvedScreen, toggleDarkMode]);

  const handleAccountMenuItemPress = useCallback(
    (itemId: string) => {
      logEvent(
        UHF_EVENT,
        buildUHFHeaderMenuParams(itemId, lob ?? null, resolvedScreen),
      );
    },
    [lob, logEvent, resolvedScreen],
  );

  const handleAccountMenuMemberIdCopy = useCallback(() => {
    logEvent(
      UHF_EVENT,
      buildUHFHeaderMenuParams('member_id_copy', lob ?? null, resolvedScreen),
    );
  }, [lob, logEvent, resolvedScreen]);

  const handleAccountMenuLogout = useCallback(() => {
    logEvent(
      UHF_EVENT,
      buildUHFHeaderMenuParams('logout', lob ?? null, resolvedScreen),
    );
  }, [lob, logEvent, resolvedScreen]);

  const handleFooterLinkPress = useCallback(
    (sectionTitle: string, linkText: string) => {
      logEvent(
        UHF_EVENT,
        buildUHFFooterParams(
          sectionTitle,
          linkText,
          lob ?? null,
          resolvedScreen,
        ),
      );
    },
    [lob, logEvent, resolvedScreen],
  );

  const handleLoginTooltipView = useCallback(() => {
    logEvent(
      'view',
      buildUHFLoginTooltipParams(
        'loginpopup',
        lob ?? null,
        resolvedScreen,
        locale,
        'view',
      ),
    );
  }, [lob, logEvent, resolvedScreen]);

  const handleLoginTooltipClose = useCallback(() => {
    logEvent(
      'click',
      buildUHFLoginTooltipParams(
        'cancel|loginpopup',
        lob ?? null,
        resolvedScreen,
        locale,
      ),
    );
  }, [lob, logEvent, resolvedScreen]);

  const handleLoginTooltipLogin = useCallback(() => {
    logEvent(
      'click',
      buildUHFLoginTooltipParams(
        'login|loginpopup',
        lob ?? null,
        resolvedScreen,
        locale,
      ),
    );
  }, [lob, logEvent, resolvedScreen]);

  return {
    handleAccountPress,
    handleAccountMenuItemPress,
    handleAccountMenuLogout,
    handleAccountMenuMemberIdCopy,
    handleDarkModeToggle,
    handleFooterLinkPress,
    handleCurrencyPress,
    handleLanguagePress,
    handleLogoPress,
    handleNavigationItemPress,
    handleLoginTooltipView,
    handleLoginTooltipClose,
    handleLoginTooltipLogin,
  };
};
